import { MutableRefObject, useCallback, useMemo, useRef, useState } from 'react';

export interface UsePromiseState<T> {
  data: T | undefined;
  busy: boolean;
  quietBusy: boolean;
  failure: boolean;
  success: boolean;
  promise?: MutableRefObject<Promise<T> | undefined>;
}

export function usePromiseState<T>(defaultValue?: T, defaultBusy = false) {
  const [data, setData] = useState<T | undefined>(defaultValue);
  const [busy, setBusy] = useState(defaultBusy);
  const [quietBusy, setQuietBusy] = useState(false);
  const [failure, setFailure] = useState(false);
  const [success, setSuccess] = useState(false);

  const promise = useRef<Promise<T> | undefined>();

  const clear = useCallback(() => {
    setBusy(false);
    setQuietBusy(false);
    setFailure(false);
    setSuccess(false);
    setData(undefined);
    promise.current = undefined;
  }, []);

  const setPromise = useCallback((newPromise: Promise<T>, quiet?: boolean) => {
    if (quiet) {
      setQuietBusy(true);
    } else {
      setBusy(true);
    }
    setFailure(false);
    setSuccess(false);

    promise.current = newPromise;

    newPromise
      .then(data => {
        if (newPromise === promise.current) {
          setData(data);
          setSuccess(true);
          setFailure(false);
        }
      })
      .catch(() => {
        setSuccess(false);
        setFailure(true);
      })
      .finally(() => {
        setBusy(false);
        setQuietBusy(false);
      });

    return newPromise;
  }, []);

  const state = useMemo(
    () => ({
      clear,
      data,
      busy,
      quietBusy,
      failure,
      success,
      promise
    }),
    [clear, data, busy, quietBusy, failure, success]
  );

  return [state, setPromise, data] as const;
}
